import isEmpty from "lodash/isEmpty";
import mapValues from "lodash/mapValues";
import omit from "lodash/omit";
import { FiltersV2AutoRefreshToggle, getVisibleFilters } from "PFApp/components/filters_v2";
import { Inline } from "PFComponents/containers";
import { Tile } from "PFComponents/tile";
import { Typography } from "PFComponents/typography";
import { FilterKeyV2, FiltersV2, KnownFiltersV2 } from "PFTypes/filters_v2";
import { RefObject, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ActivityPageAvailabilityFilter } from "./parts/activity_page_availability_filter";
import { ActivityPageFilters } from "./parts/activity_page_filters";
import { FiltersFooter } from "./parts/filters_footer";
import css from "./side_filters.module.scss";

const OMITTED_FILTERS: FilterKeyV2[] = [KnownFiltersV2.Term, KnownFiltersV2.Availability];

const getOnlySidePanelFiltersWithDisabledFlag = (filters: FiltersV2, disabled?: boolean) =>
  mapValues(omit(getVisibleFilters(filters), OMITTED_FILTERS), (filter) => ({ ...filter, disabled }));

export type SideFiltersProps = {
  disabled?: boolean;
  moreFiltersTitle?: string;
  clearMoreFiltersTitle?: string;
  restoreMoreFiltersTitle?: string;
  scrollableContainerRef: RefObject<HTMLDivElement | null>;
  activityFilters?: FiltersV2;
  filters: FiltersV2;
  scope?: number;
};

export const ActivitySidePanelFilters = ({
  disabled = false,
  moreFiltersTitle,
  clearMoreFiltersTitle,
  restoreMoreFiltersTitle,
  scrollableContainerRef,
  filters,
  activityFilters,
  scope
}: SideFiltersProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "show.sidePanel.filters" });

  const filtersToDisplay = useMemo(
    () => getOnlySidePanelFiltersWithDisabledFlag(filters, disabled),
    [disabled, filters]
  );
  const activityFiltersToDisplay = useMemo(
    () => activityFilters && getOnlySidePanelFiltersWithDisabledFlag(activityFilters, disabled),
    [activityFilters, disabled]
  );

  return (
    <Tile className={css.root} paddingVariant="lg" variant="light">
      <Inline as="header" alignItems="center" justifyContent="space-between">
        <Typography variant="h4">{t("title")}</Typography>
        <FiltersV2AutoRefreshToggle />
      </Inline>
      <ActivityPageAvailabilityFilter disabled={disabled} scope={scope || 0} />
      {!isEmpty(activityFiltersToDisplay) && (
        <ActivityPageFilters
          filters={activityFiltersToDisplay}
          title={t("activityFilters")}
          clearTitle={t("clearActivityFilters")}
          restoreTitle={t("restoreActivityFilters")}
          disabled={disabled}
        />
      )}
      {!isEmpty(filtersToDisplay) && (
        <ActivityPageFilters
          filters={filtersToDisplay}
          disabled={disabled}
          title={moreFiltersTitle ?? t("moreFilters")}
          clearTitle={clearMoreFiltersTitle ?? t("clearMoreFilters")}
          restoreTitle={restoreMoreFiltersTitle ?? t("restoreMoreFilters")}
        />
      )}
      <FiltersFooter scrollableContainerRef={scrollableContainerRef} />
    </Tile>
  );
};
